import { createRoot } from 'react-dom/client'
import { useState, useEffect } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import 'antd/dist/reset.css'
import Router from '@/router'
import { BrowserRouter } from 'react-router-dom'
import { ConfigProvider, message, notification } from 'antd'
import type { Locale } from 'antd/es/locale'
import enUS from 'antd/locale/en_US'
import zhCN from 'antd/locale/zh_CN'
import jaJP from 'antd/locale/ja_JP'
import koKR from 'antd/locale/ko_KR'
import frFR from 'antd/locale/fr_FR'
import ProgressBar from '@/components/progressBar/progressBar'
import '@/i18n' // Initialize i18n
import { languages } from '@/i18n'
import { getUserInfo } from '@/request/api'
import { useTranslation } from 'react-i18next'
import './base.css'
import './styles/gh-modal.scss'

// Map i18n language codes to Ant Design locales
const antdLocales: Record<string, Locale> = {
	en: enUS,
	zh: zhCN,
	ja: jaJP,
	ko: koKR,
	fr: frFR,
}

// Global message & notification configuration
message.config({
	top: 64,
	duration: 3,
	maxCount: 3,
	rtl: false,
})

notification.config({
	placement: 'topRight',
	top: 64,
	duration: 3,
})

// Wrapper component to handle dynamic locale
const App = () => {
	const { i18n } = useTranslation()
	const [antdLocale, setAntdLocale] = useState<Locale>(antdLocales[i18n.language] || enUS)
	
	useEffect(() => {
		// Update Ant Design locale when i18n language changes
		const handleLanguageChange = (lng: string) => {
			setAntdLocale(antdLocales[lng] || enUS)
		}
		
		i18n.on('languageChanged', handleLanguageChange)
		
		return () => {
			i18n.off('languageChanged', handleLanguageChange)
		}
	}, [i18n])

	useEffect(() => {
		let cancelled = false
		getUserInfo().then((res) => {
			if (cancelled || res.code !== 0) return
			const userInfo = { email: res.email, role: res.role || 'user' }
			localStorage.setItem('userInfo', JSON.stringify(userInfo))
			window.dispatchEvent(new Event('userInfoChanged'))
			if (res.language && languages.some(lang => lang.code === res.language)) {
				localStorage.setItem('etherworks-language', res.language)
				if (res.language !== i18n.language) i18n.changeLanguage(res.language)
			}
		}).catch((error) => {
			console.error('Failed to load language preference:', error)
		})
		return () => { cancelled = true }
	}, [i18n])
	
	return (
		<ConfigProvider locale={antdLocale}>
			<ProgressBar />
			<Router />
		</ConfigProvider>
	)
}

const container = document.getElementById('root')
if (!container) {
	throw new Error('Failed to find the root element')
}

const root = createRoot(container)
root.render(
	<HelmetProvider>
		<BrowserRouter>
			<App />
		</BrowserRouter>
	</HelmetProvider>
)